import { useCallback, useContext } from "react";
import { Id, toast } from "react-toastify";
import { ApiClientResponseError } from "@verity/api-client";
import { AssessmentInfo, PlatformInfo, VerityOptions } from "@verity/api-client/types";
import { coreToast } from "@verity/components/core";
import apiClient from "../helpers/api-client";
import { PortalContext } from "./portal-config";
import { PortalControllerContext } from "./context";
import i18n from "../i18n";
import { isChromeOsDevice, isMobileDevice, isPlatformCompatible } from "../helpers/device";
import { isIncognitoAllowed, isInManagedGuestSession, compatibilityCheckInProgress } from "../setup/helpers/extension";
import { PORTAL_TOAST_CONTAINER_ID, replaceWithPortalHostname } from "./shared";
import { QuizWithProctoringOptions } from "./components/assessments-page/assessments-page-helper";
import { dispatchProxyMessage } from "@verity/extension/proxy-message/external";
import { isPortalCreatedThirdPartyAssessment } from "../helpers/assessment-name";
import { getInferredBrowserExtensionId, getInferredBrowserArch } from "@verity/extension/platform";

export type CombinedAssessmentInfo = AssessmentInfo & {
	lmsProvidedCourseId: string;
	lmsProvidedAssessmentId: string;
};

export type LockdownLaunchInfo = {
	target: string;
	thirdPartyOptions: VerityOptions["thirdPartyAssessmentOptions"];
	assessmentIdInfo?: CombinedAssessmentInfo;
};
export type MobileLaunchInfo = LockdownLaunchInfo;

type ThirdPartyOptions = VerityOptions["thirdPartyAssessmentOptions"];
type ValidThirdPartyOptions = Exclude<
	ThirdPartyOptions & {
		thirdPartyUrl: string;
	},
	null | undefined
>;

function constructAssessmentInfo(portalContext: PortalContext, quizId: string): CombinedAssessmentInfo {
	const { lms, instituteIntegrationHostname, lmsProvidedCourseId } = portalContext;
	if (!lmsProvidedCourseId) {
		throw new Error("No course ID provided");
	}
	return {
		lms,
		hostname: instituteIntegrationHostname,
		quizId,
		courseId: lmsProvidedCourseId,
		lmsProvidedCourseId,
		lmsProvidedAssessmentId: quizId,
	};
}

function launchAssessmentUrl(url: string, inMobile = false, target: "_blank" | "_top" = "_blank") {
	if (inMobile && window.top) {
		window.top.location = url;
	} else {
		const assessmentWindow = window.open(url, target);
		const browserArch = getInferredBrowserArch();
		if (browserArch !== "chromium") {
			assessmentWindow?.addEventListener(
				"load",
				() => {
					assessmentWindow.location.reload();
					assessmentWindow?.focus();
				},
				{ once: true },
			);
		} else {
			assessmentWindow?.focus();
		}
	}
}

async function launchThirdPartyAssessmentExtension(
	link: string,
	assessmentIdInfo: CombinedAssessmentInfo,
	thirdPartyOptions: ValidThirdPartyOptions,
	loadingToastId: Id,
	target: "_blank" | "_top" = "_blank",
) {
	let isToastUpdated = false;
	try {
		const handler = (res: any) => {
			if (!res) return;
			if (res.success) {
				launchAssessmentUrl(link, false, target);
				toast.dismiss(loadingToastId);
			} else {
				console.error(res.reason);
				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
				if (
					res.reason.includes("Duplicate script ID") ||
					res.reason.includes("is already registered") ||
					res.reason.includes("Duplicate ID")
				) {
					launchAssessmentUrl(link, false, target);
					toast.dismiss(loadingToastId);
				} else {
					showUnknownErrorToast(loadingToastId);
				}
			}
			isToastUpdated = true;
		};

		if (window.chrome) {
			chrome.runtime.sendMessage(
				getInferredBrowserExtensionId(),
				{
					request: "startThirdPartyAssessmentScript",
					assessmentUrl: thirdPartyOptions.thirdPartyUrl,
					thirdPartyPlatformId: thirdPartyOptions.thirdPartyPlatformId,
					quizInfo: assessmentIdInfo,
				},
				handler,
			);
		} else {
			dispatchProxyMessage({
				request: "startThirdPartyAssessmentScript",
				assessmentUrl: thirdPartyOptions.thirdPartyUrl,
				thirdPartyPlatformId: thirdPartyOptions.thirdPartyPlatformId,
				quizInfo: assessmentIdInfo,
			})
				.then(handler)
				.catch(console.error);
		}
	} catch {
		isToastUpdated = true;
		coreToast.update(loadingToastId, i18n.t("portal.components.startAssessment.extensionError"), "error", {
			containerId: PORTAL_TOAST_CONTAINER_ID,
		});
	}
	setTimeout(async () => {
		if (!isToastUpdated) {
			coreToast.update(loadingToastId, i18n.t("portal.components.startAssessment.extensionError"), "error", {
				containerId: PORTAL_TOAST_CONTAINER_ID,
			});
		}
	}, 3000);
}

async function launchThirdPartyAssessmentLockdown(
	link: string,
	assessmentIdInfo: CombinedAssessmentInfo,
	thirdPartyOptions: ValidThirdPartyOptions,
	loadingToastId: Id,
) {
	await new Promise((resolve, reject) => {
		const listener = (ev: MessageEvent) => {
			if (ev.data.type === "setPendingAssessment") {
				window.removeEventListener("message", listener);
				if (ev.data.error) {
					reject(ev.data.error);
				} else {
					resolve(ev.data.data);
				}
			}
		};
		window.addEventListener("message", listener);
		window.top?.postMessage(
			{
				type: "setPendingAssessment",
				data: {
					assessmentInfo: {
						lms: assessmentIdInfo.lms,
						hostname: assessmentIdInfo.hostname,
						lmsProvidedCourseId: assessmentIdInfo.courseId,
						lmsProvidedAssessmentId: assessmentIdInfo.quizId,
					},
					thirdPartyUrl: link,
					hasThirdPartyPassword: !!thirdPartyOptions?.password,
				},
			},
			"*",
		);
	});
	toast.dismiss(loadingToastId);
}

function isValidThirdPartyOptions(thirdPartyOptions: ThirdPartyOptions): thirdPartyOptions is ValidThirdPartyOptions {
	return !!thirdPartyOptions?.thirdPartyUrl;
}

function showUnknownErrorToast(loadingToastId?: Id) {
	coreToast.updateOrShow(loadingToastId, i18n.t("portal.components.startAssessment.unknownError"), "error", {
		containerId: PORTAL_TOAST_CONTAINER_ID,
	});
}

function handleLaunchError(err: unknown, portalContext: PortalContext, loadingToastId: Id) {
	console.error(err);
	if (err instanceof Error) {
		if (err.message === "noUrl") {
			coreToast.update(loadingToastId, i18n.t("portal.components.startAssessment.noThirdPartyUrl"), "error", {
				containerId: PORTAL_TOAST_CONTAINER_ID,
			});
			return;
		} else if (err instanceof ApiClientResponseError && err.status === 501) {
			if (portalContext.lms === "BlackboardLearn") {
				coreToast.update(loadingToastId, i18n.t("portal.components.startAssessment.blackboardSupportError"), "error", {
					containerId: PORTAL_TOAST_CONTAINER_ID,
				});
			} else {
				coreToast.update(loadingToastId, i18n.t("portal.components.startAssessment.lmsError"), "error", {
					containerId: PORTAL_TOAST_CONTAINER_ID,
				});
			}
			return;
		}
	}
	showUnknownErrorToast(loadingToastId);
}

export async function launchAssessmentHere(
	portalContext: PortalContext,
	userPlatformInfo: PlatformInfo,
	assessment: QuizWithProctoringOptions,
	target: "_blank" | "_top" = "_blank",
) {
	const { quizId, proctoringOptions } = assessment;
	const { lms, instituteId, instituteIntegrationHostname, portalHostname } = portalContext;
	const inLockdown = userPlatformInfo.platform === "lockdown";
	const inMobile = userPlatformInfo.platform === "mobile";

	if (instituteId == null) {
		showUnknownErrorToast();
		return;
	}
	const assessmentIdInfo = constructAssessmentInfo(portalContext, quizId);
	const loadingToastId = coreToast.loading(i18n.t("portal.components.startAssessment.loadingToast"), {
		containerId: PORTAL_TOAST_CONTAINER_ID,
	});

	try {
		let link = replaceWithPortalHostname(
			await apiClient.lmsInfo.getAssessmentUrl(
				assessmentIdInfo.lmsProvidedCourseId,
				assessmentIdInfo.lmsProvidedAssessmentId,
				"view",
			),
			instituteIntegrationHostname,
			portalHostname,
		);
		const thirdPartyOptions =
			assessment.proctoringAssessmentConfiguration?.thirdPartyAssessmentOption ??
			(await apiClient.verityOptions.get(assessmentIdInfo)).thirdPartyAssessmentOptions;
		const isPortalCreatedThirdParty = isPortalCreatedThirdPartyAssessment(assessment);

		if (thirdPartyOptions?.thirdPartyUrl && lms === "BlackboardLearn")
			link = link.replace(/(\/ultra\/courses\/[^/]+\/outline).*/, "$1");
		if (isPortalCreatedThirdParty && thirdPartyOptions?.thirdPartyUrl) link = thirdPartyOptions.thirdPartyUrl;

		if (thirdPartyOptions) {
			if (isValidThirdPartyOptions(thirdPartyOptions)) {
				if (!proctoringOptions?.enabled) {
					launchAssessmentUrl(link, inMobile, target);
					toast.dismiss(loadingToastId);
					return;
				}
				if (inLockdown) {
					if (window.top) {
						await launchThirdPartyAssessmentLockdown(link, assessmentIdInfo, thirdPartyOptions, loadingToastId);
					}
				} else {
					await launchThirdPartyAssessmentExtension(link, assessmentIdInfo, thirdPartyOptions, loadingToastId, target);
				}
			} else {
				throw new Error("No third-party URL");
			}
		} else if (lms) {
			const lmsCourseId = portalContext.lmsProvidedCourseId;
			if (lmsCourseId && inMobile) {
				const token = await apiClient.lmsInfo.getSessionToken(lmsCourseId);
				if (token) link += `?session_token=${token}`;
			}
			launchAssessmentUrl(link, inMobile, target);
			toast.dismiss(loadingToastId);
		} else {
			showUnknownErrorToast(loadingToastId);
		}
	} catch (e) {
		handleLaunchError(e, portalContext, loadingToastId);
	}
}

export async function getLockdownLaunchInfo(
	portalContext: PortalContext,
	assessment: QuizWithProctoringOptions,
): Promise<LockdownLaunchInfo> {
	const { quizId } = assessment;
	const { lms, instituteIntegrationHostname, portalHostname } = portalContext;

	const assessmentIds = constructAssessmentInfo(portalContext, quizId);

	let target = replaceWithPortalHostname(
		await apiClient.lmsInfo.getAssessmentUrl(
			assessmentIds.lmsProvidedCourseId,
			assessmentIds.lmsProvidedAssessmentId,
			"view",
		),
		instituteIntegrationHostname,
		portalHostname,
	);
	const thirdPartyOptions =
		assessment.proctoringAssessmentConfiguration?.thirdPartyAssessmentOption ??
		(await apiClient.verityOptions.get(assessmentIds)).thirdPartyAssessmentOptions;

	if (thirdPartyOptions) {
		if (isValidThirdPartyOptions(thirdPartyOptions) && isPortalCreatedThirdPartyAssessment(assessment)) {
			target = thirdPartyOptions.thirdPartyUrl;
		} else if (!isValidThirdPartyOptions(thirdPartyOptions)) {
			throw new Error("No third-party URL");
		}
	}

	if (target && !thirdPartyOptions && lms === "BlackboardLearn") {
		const targetUrl = new URL(target);
		target = `https://${targetUrl.hostname}?new_loc=${encodeURIComponent(target.replace(targetUrl.origin, ""))}`;
	}

	return {
		target,
		thirdPartyOptions,
		assessmentIdInfo: assessmentIds,
	};
}

export async function launchLockdown(launchInfo: LockdownLaunchInfo, loadingToastId?: Id) {
	const { target, thirdPartyOptions, assessmentIdInfo } = launchInfo;
	try {
		let accountId: string | undefined;
		let authCode: string | undefined;
		try {
			({ accountId, authCode } = await apiClient.authentication.getAccessControlAuthCode());
		} catch (e) {
			console.error(e);
		}

		const searchParams = new URLSearchParams();
		if (accountId && authCode) {
			searchParams.set("accountId", accountId);
			searchParams.set("code", authCode);
		}
		searchParams.set("target", target);
		if (thirdPartyOptions) {
			searchParams.set("thirdPartyUrl", target);
			searchParams.set("hasThirdPartyPassword", String(!!thirdPartyOptions?.password));
		}
		if (assessmentIdInfo) {
			if (assessmentIdInfo.lms) {
				searchParams.set("lms", assessmentIdInfo.lms);
			}
			if (assessmentIdInfo.hostname) {
				searchParams.set("hostname", assessmentIdInfo.hostname);
			}
			searchParams.set("lmsProvidedCourseId", assessmentIdInfo.lmsProvidedCourseId);
			searchParams.set("lmsProvidedAssessmentId", assessmentIdInfo.lmsProvidedAssessmentId);
		}
		window.location.href = `veritylockdown://launch?${searchParams.toString()}`;
		if (loadingToastId) {
			toast.dismiss(loadingToastId);
		}
	} catch (e) {
		console.error(e);
		coreToast.updateOrShow(loadingToastId, i18n.t("portal.components.startAssessment.authError"), "error", {
			containerId: PORTAL_TOAST_CONTAINER_ID,
		});
	}
}

export default function useStartAssessment(
	assessment: QuizWithProctoringOptions,
	showExtensionSetup: (reason: "extension" | "managed-guest-session" | "incognito") => void,
	onLaunchLockdown: (launchInfo: LockdownLaunchInfo) => void,
	target: "_blank" | "_top" = "_blank",
) {
	const { proctoringOptions } = assessment;
	const { enabled, isExtension, isLockdown, isMobile, chromeOsAccess, requiresIncognito } = proctoringOptions ?? {};

	const { portalContext, userPlatformInfo, browserType, instituteSettings, navigateToPage } =
		useContext(PortalControllerContext);

	const extensionCompatible = isPlatformCompatible("extension", browserType);
	const lockdownCompatible = isPlatformCompatible("lockdown", browserType);
	const mobileCompatible = isPlatformCompatible("mobile", browserType);

	const inMobileDevice = isMobileDevice();
	const inChromeOs = isChromeOsDevice();
	const inExtension = userPlatformInfo.platform === "extension";
	const inLockdown = userPlatformInfo.platform === "lockdown";
	const inMobile = userPlatformInfo.platform === "mobile";

	const shouldUseExtensionAsLockdown = enabled && inChromeOs && isLockdown && !isExtension && chromeOsAccess;

	const startAssessment = useCallback(async () => {
		if (
			!enabled ||
			(isExtension && inExtension && userPlatformInfo.versionSupported) ||
			(isLockdown &&
				(inLockdown || (shouldUseExtensionAsLockdown && inExtension && userPlatformInfo.versionSupported))) ||
			(isMobile && inMobile)
		) {
			const requiresManagedGuestSession =
				enabled && shouldUseExtensionAsLockdown && inExtension && chromeOsAccess === "managed-guest-session";
			let inManagedGuestSession = false;
			if ((enabled && inChromeOs && inExtension && requiresIncognito) || requiresManagedGuestSession) {
				try {
					inManagedGuestSession = await isInManagedGuestSession(getInferredBrowserExtensionId());
				} catch (e) {
					console.error(e);
				}
			}

			if (requiresManagedGuestSession && !inManagedGuestSession) {
				showExtensionSetup("managed-guest-session");
			} else if (
				enabled &&
				inExtension &&
				requiresIncognito &&
				(instituteSettings.managedGuestSessionRequireIncognito || !inManagedGuestSession) &&
				!(await isIncognitoAllowed(getInferredBrowserExtensionId()).catch(() => false))
			) {
				showExtensionSetup("incognito");
			} else {
				await launchAssessmentHere(portalContext, userPlatformInfo, assessment, target);
			}
		} else if (isLockdown && lockdownCompatible) {
			const loadingToastId = coreToast.loading(i18n.t("portal.components.startAssessment.openToast"), {
				containerId: PORTAL_TOAST_CONTAINER_ID,
			});
			try {
				const launchInfo = await getLockdownLaunchInfo(portalContext, assessment);
				onLaunchLockdown(launchInfo);
				await launchLockdown(launchInfo, loadingToastId);
			} catch (e) {
				handleLaunchError(e, portalContext, loadingToastId);
			}
		} else if (isMobile && mobileCompatible) {
			const assessmentIdInfo = constructAssessmentInfo(portalContext, assessment.quizId);
			const link = replaceWithPortalHostname(
				await apiClient.lmsInfo.getAssessmentUrl(assessmentIdInfo.lmsProvidedCourseId, assessmentIdInfo.quizId, "view"),
				portalContext.instituteIntegrationHostname,
				portalContext.portalHostname,
			);
			localStorage.setItem("currentAssessmentUrl", link);
			navigateToPage("mobileWorkflow");
			showExtensionSetup("extension");
		} else if ((isExtension || shouldUseExtensionAsLockdown) && extensionCompatible) {
			showExtensionSetup("extension");
		} else {
			// shouldn't get here
			showUnknownErrorToast();
		}
	}, [
		portalContext,
		inMobileDevice,
		inChromeOs,
		userPlatformInfo,
		assessment,
		extensionCompatible,
		lockdownCompatible,
		mobileCompatible,
		instituteSettings,
	]);

	return !enabled ||
		((isExtension || (isLockdown && shouldUseExtensionAsLockdown)) && extensionCompatible) ||
		(isLockdown && lockdownCompatible) ||
		(isMobile && mobileCompatible)
		? startAssessment
		: undefined;
}

export async function checkCompatibilityExtension(portalContext: PortalContext, assessment: QuizWithProctoringOptions) {
	const assessmentIdInfo = constructAssessmentInfo(portalContext, assessment.quizId);
	if (window.chrome) {
		await chrome.runtime.sendMessage(getInferredBrowserExtensionId(), {
			request: "startCompatibilityCheck",
			quizInfo: assessmentIdInfo,
		});
	} else {
		await dispatchProxyMessage({
			request: "startCompatibilityCheck",
			quizInfo: assessmentIdInfo,
		});
	}
	launchAssessmentUrl(`${window.location.origin}/portal/webpages/compatibility-check.html`);
}

function getLockdownCompatibilityCheckLaunchInfo({ id }: QuizWithProctoringOptions): LockdownLaunchInfo {
	return {
		target: `verity://check-compatibility#${id}`,
		assessmentIdInfo: undefined,
		thirdPartyOptions: undefined,
	};
}

export function useCheckCompatibility(
	assessment: QuizWithProctoringOptions,
	showExtensionSetup: (reason: "extension" | "managed-guest-session" | "incognito") => void,
	onLaunchLockdown: (launchInfo: LockdownLaunchInfo) => void,
	onError: (reason: "in-progress") => void,
) {
	const { proctoringOptions } = assessment;
	const { enabled, isExtension, isLockdown, chromeOsAccess, requiresIncognito } = proctoringOptions ?? {};

	const { browserType, userPlatformInfo, portalContext, instituteSettings } = useContext(PortalControllerContext);

	const extensionCompatible = isPlatformCompatible("extension", browserType);
	const lockdownCompatible = isPlatformCompatible("lockdown", browserType);

	const inChromeOs = isChromeOsDevice();
	const inExtension = userPlatformInfo.platform === "extension";
	const inLockdown = userPlatformInfo.platform === "lockdown";

	const shouldUseExtensionAsLockdown = inChromeOs && isLockdown && !isExtension && chromeOsAccess;

	const checkCompatibility = useCallback(async () => {
		if (inExtension && (await compatibilityCheckInProgress(getInferredBrowserExtensionId()))) {
			onError("in-progress");
			return;
		}

		const loadingToastId = coreToast.loading(i18n.t("portal.components.startAssessment.openToast"), {
			containerId: PORTAL_TOAST_CONTAINER_ID,
		});
		try {
			const lockdownLaunchInfo = getLockdownCompatibilityCheckLaunchInfo(assessment);
			if (inLockdown && isLockdown) {
				launchAssessmentUrl(lockdownLaunchInfo.target);
			} else if (inExtension && (isExtension || shouldUseExtensionAsLockdown)) {
				const requiresManagedGuestSession = shouldUseExtensionAsLockdown && chromeOsAccess === "managed-guest-session";
				let inManagedGuestSession = false;
				if ((inChromeOs && requiresIncognito) || requiresManagedGuestSession) {
					try {
						inManagedGuestSession = await isInManagedGuestSession(getInferredBrowserExtensionId());
					} catch (e) {
						console.error(e);
					}
				}

				if (requiresManagedGuestSession && !inManagedGuestSession) {
					showExtensionSetup("managed-guest-session");
				} else if (
					requiresIncognito &&
					(instituteSettings.managedGuestSessionRequireIncognito || !inManagedGuestSession) &&
					!(await isIncognitoAllowed(getInferredBrowserExtensionId()).catch(() => false))
				) {
					showExtensionSetup("incognito");
				} else {
					await checkCompatibilityExtension(portalContext, assessment);
				}
			} else if ((isExtension || shouldUseExtensionAsLockdown) && extensionCompatible) {
				showExtensionSetup("extension");
			} else if (isLockdown && lockdownCompatible) {
				onLaunchLockdown(lockdownLaunchInfo);
				await launchLockdown(lockdownLaunchInfo);
			} else {
				// shouldn't get here
				showUnknownErrorToast(loadingToastId);
				return;
			}
			toast.dismiss(loadingToastId);
		} catch (e) {
			console.error(e);
			showUnknownErrorToast(loadingToastId);
		}
	}, [userPlatformInfo, assessment, extensionCompatible, lockdownCompatible, inChromeOs, instituteSettings]);

	return enabled &&
		(((isExtension || shouldUseExtensionAsLockdown) && extensionCompatible) || (isLockdown && lockdownCompatible))
		? checkCompatibility
		: undefined;
}
